import { useState } from 'react'
import type { DatabaseConnection } from '@/stores/connectionStore'
import { useToast } from '@/hooks/useToast'
import { AgentProxyService } from '@/services/agentProxyService'
import { useQueryClient } from '@tanstack/react-query'
import { databaseKeys } from '@/hooks/useDatabase'
import {
  PlusIcon,
  XMarkIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline'

interface CreateFunctionViewProps {
  connection: DatabaseConnection
  database: string
  schema?: string
  onFunctionCreated: () => void
  duplicateFrom?: {
    originalName: string
    newName: string
    definition: unknown
  }
}

interface FunctionParameter {
  name: string
  type: string
  mode: 'IN' | 'OUT' | 'INOUT'
  defaultValue?: string
}

// Utility function to extract function body from CREATE statement
function extractFunctionBody(definition: string): string {
  if (!definition) return ''
  
  console.log('Extracting function body from:', `${definition.substring(0, 200)}...`)
  
  // MySQL-style: Extract body from BEGIN ... END blocks
  const mysqlBeginEndMatch = definition.match(/BEGIN\s+([\s\S]*?)\s+END(?:\s*;?\s*$)/i)
  if (mysqlBeginEndMatch && mysqlBeginEndMatch[1]) {
    console.log('Found MySQL BEGIN...END block, body:', `${mysqlBeginEndMatch[1].substring(0, 100)}...`)
    return mysqlBeginEndMatch[1].trim()
  }
  
  // PostgreSQL-style: Handle tagged dollar quoting: AS $tag$ ... $tag$
  const taggedMatch = definition.match(/AS\s+\$([^$\s]+)\$\s*([\s\S]*?)\s*\$\1\$/i)
  if (taggedMatch && taggedMatch[2]) {
    console.log('Found tagged dollar quoting, body:', `${taggedMatch[2].substring(0, 100)}...`)
    return taggedMatch[2].trim()
  }
  
  // PostgreSQL-style: Handle double dollar quoting: AS $$ ... $$
  const doubleDollarMatch = definition.match(/AS\s+\$\$\s*([\s\S]*?)\s*\$\$/i)
  if (doubleDollarMatch && doubleDollarMatch[1]) {
    console.log('Found double dollar quoting, body:', `${doubleDollarMatch[1].substring(0, 100)}...`)
    return doubleDollarMatch[1].trim()
  }
  
  // PostgreSQL-style: Handle single dollar quoting: AS $ ... $ (most common case for your error)
  // Look for AS $ ... $ followed by LANGUAGE or end
  const singleDollarMatch = definition.match(/AS\s+\$\s*([\s\S]*?)\s*\$\s*(?:LANGUAGE|$)/i)
  if (singleDollarMatch && singleDollarMatch[1]) {
    console.log('Found single dollar quoting, body:', `${singleDollarMatch[1].substring(0, 100)}...`)
    return singleDollarMatch[1].trim()
  }
  
  // Fallback: look for AS followed by quoted content
  const asQuotedMatch = definition.match(/AS\s+['"]([^'"]*)['"]/i)
  if (asQuotedMatch && asQuotedMatch[1]) {
    console.log('Found quoted AS, body:', `${asQuotedMatch[1].substring(0, 100)}...`)
    return asQuotedMatch[1].trim()
  }
  
  // If no AS pattern found, check if this is already just the body
  if (!definition.toUpperCase().includes('CREATE')) {
    console.log('No CREATE found, treating as body:', `${definition.substring(0, 100)}...`)
    return definition.trim()
  }
  
  // EMERGENCY FALLBACK: If we still have a CREATE statement, try to extract everything between AS and LANGUAGE
  const emergencyMatch = definition.match(/AS\s+([\s\S]*?)\s*LANGUAGE/i)
  if (emergencyMatch && emergencyMatch[1]) {
    // Remove any dollar signs from the edges
    let body = emergencyMatch[1].trim()
    body = body.replace(/^\$+/, '').replace(/\$+$/, '').trim()
    console.log('Emergency fallback extracted body:', `${body.substring(0, 100)}...`)
    return body
  }
  
  console.log('Could not extract function body')
  return ''
}

// Utility function to extract return type from CREATE statement
function extractReturnType(definition: string): string {
  const returnsMatch = definition.match(/RETURNS\s+([^\s]+)/i)
  return returnsMatch ? returnsMatch[1].toLowerCase() : 'void'
}

// Utility function to extract language from CREATE statement  
function extractLanguage(definition: string): string {
  const languageMatch = definition.match(/LANGUAGE\s+([^\s]+)/i)
  return languageMatch ? languageMatch[1].toLowerCase() : 'plpgsql'
}

export function CreateFunctionView({ connection, database, schema, onFunctionCreated, duplicateFrom }: CreateFunctionViewProps) {
  // Initialize state based on whether we're duplicating or creating new
  const [functionName, setFunctionName] = useState(duplicateFrom?.newName || '')
  const [returnType, setReturnType] = useState(() => {
    if (duplicateFrom?.definition) {
      try {
        const def = duplicateFrom.definition as unknown
        // First try to get from metadata
        if (def && typeof def === 'object' && 'metadata' in def && 
            def.metadata && typeof def.metadata === 'object' && 
            'return_type' in def.metadata) {
          return (def.metadata.return_type as string) || 'void'
        }
        // Fallback: parse from definition string
        if (def && typeof def === 'object' && 'definition' in def) {
          return extractReturnType(def.definition as string)
        }
        if (typeof def === 'string') {
          return extractReturnType(def)
        }
      } catch {
        return 'void'
      }
    }
    return 'void'
  })
  const [language, setLanguage] = useState(() => {
    if (duplicateFrom?.definition) {
      try {
        const def = duplicateFrom.definition as unknown
        // First try to get from metadata
        if (def && typeof def === 'object' && 'metadata' in def && 
            def.metadata && typeof def.metadata === 'object' && 
            'language' in def.metadata) {
          return (def.metadata.language as string) || 'plpgsql'
        }
        // Fallback: parse from definition string
        if (def && typeof def === 'object' && 'definition' in def) {
          const d = (def as { definition?: unknown }).definition
          return typeof d === 'string' ? extractLanguage(d) : 'plpgsql'
        }
        if (typeof def === 'string') {
          return extractLanguage(def)
        }
      } catch {
        return 'plpgsql'
      }
    }
    return 'plpgsql'
  })
  const [parameters, setParameters] = useState<FunctionParameter[]>(() => {
    if (duplicateFrom?.definition) {
      try {
        const def = duplicateFrom.definition as unknown
        if (def && typeof def === 'object' && 'metadata' in def && 
            def.metadata && typeof def.metadata === 'object' && 
            'parameters' in def.metadata && Array.isArray(def.metadata.parameters)) {
          return def.metadata.parameters.map((param: unknown) => {
            const get = (obj: unknown, key: string): unknown => (obj && typeof obj === 'object' && !Array.isArray(obj)) ? (obj as Record<string, unknown>)[key] : undefined
            const name = get(param, 'column_name')
            const fullType = get(param, 'full_data_type')
            const dataType = get(param, 'data_type')
            const modeVal = get(param, 'column_type')
            const defaultVal = get(param, 'default_value')
            const modeCandidate = typeof modeVal === 'string' && ['IN','OUT','INOUT'].includes(modeVal.toUpperCase()) ? (modeVal.toUpperCase() as 'IN'|'OUT'|'INOUT') : 'IN'
            return {
              name: typeof name === 'string' && name ? name : 'param',
              type: typeof fullType === 'string' && fullType ? fullType : (typeof dataType === 'string' && dataType ? dataType : 'integer'),
              mode: modeCandidate,
              defaultValue: typeof defaultVal === 'string' ? defaultVal : ''
            }
          })
        }
      } catch {
        // Fall through to empty array
      }
    }
    return []
  })
  const [functionBody, setFunctionBody] = useState(() => {
    if (duplicateFrom?.definition) {
      try {
        const def = duplicateFrom.definition as unknown
        console.log('Full definition object:', def)
        
        let rawDefinition = ''
        if (def && typeof def === 'object') {
          if ('metadata' in def && def.metadata && typeof def.metadata === 'object' && 'definition' in def.metadata) {
            const md = def.metadata as { definition?: unknown }
            rawDefinition = typeof md.definition === 'string' ? md.definition : ''
            console.log('Using metadata.definition:', `${rawDefinition.substring(0, 200)}...`)
          } else if ('definition' in def) {
            const d = (def as { definition?: unknown }).definition
            rawDefinition = typeof d === 'string' ? d : ''
            console.log('Using definition:', `${rawDefinition.substring(0, 200)}...`)
          }
        } else if (typeof def === 'string') {
          rawDefinition = def
          console.log('Using string definition:', `${rawDefinition.substring(0, 200)}...`)
        }
        
        if (rawDefinition) {
          const extractedBody = extractFunctionBody(rawDefinition)
          console.log('Extracted body:', `${extractedBody.substring(0, 200)}...`)
          return extractedBody
        }
      } catch (error) {
        console.error('Error processing function definition:', error)
        return ''
      }
    }
    return ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  const { showToast } = useToast()
  const queryClient = useQueryClient()

  const addParameter = () => {
    setParameters([...parameters, { name: '', type: 'integer', mode: 'IN' }])
  }

  const removeParameter = (index: number) => {
    setParameters(parameters.filter((_, i) => i !== index))
  }

  const updateParameter = (index: number, field: keyof FunctionParameter, value: string) => {
    const newParameters = Array.from(parameters)
    newParameters[index] = { ...newParameters[index], [field]: value }
    setParameters(newParameters)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!functionName.trim() || !functionBody.trim()) {
      showToast('error', 'Please fill in function name and body')
      return
    }

    setIsSubmitting(true)
    
    try {
      // The backend constructs "CREATE OR REPLACE FUNCTION schema.name" and appends our definition
      // So we send just the signature and body, without CREATE FUNCTION functionName
      const paramString = parameters.map(param => {
        let paramStr = `${param.mode} ${param.name} ${param.type}`
        if (param.defaultValue) {
          paramStr += ` DEFAULT ${param.defaultValue}`
        }
        return paramStr
      }).join(', ')

      const functionDefinition = isDatabasePostgreSQL
        ? `(${paramString})
RETURNS ${returnType}
LANGUAGE ${language}
AS $$
${functionBody}
$$;`
        : `(${paramString})
RETURNS ${returnType}
DETERMINISTIC
BEGIN
${functionBody}
END;`

      console.log('Function definition being sent to backend:', functionDefinition)

      // Create function using agent-proxy - send the signature and body (without CREATE FUNCTION name)
      await AgentProxyService.createFunction(
        connection.id,
        database,
        {
          function_name: functionName,
          function_definition: functionDefinition
        },
        schema
      )
      
      showToast('success', `Function "${functionName}" created successfully`)
      
      // Invalidate functions queries to refresh UI
      await queryClient.invalidateQueries({ 
        queryKey: [...databaseKeys.all, 'functions', connection.id, database, schema] 
      })
      
      onFunctionCreated()
    } catch (error) {
      console.error('Failed to create function:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to create function'
      showToast('error', errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const isDatabasePostgreSQL = connection.type === 'postgresql'
  const isDatabaseMySQL = connection.type === 'mysql'

  const commonDataTypes = isDatabasePostgreSQL
    ? ['integer', 'bigint', 'text', 'varchar', 'boolean', 'date', 'timestamp', 'numeric', 'json', 'jsonb']
    : ['INT', 'BIGINT', 'VARCHAR(255)', 'TEXT', 'BOOLEAN', 'DATE', 'DATETIME', 'DECIMAL', 'JSON']

  const returnTypes = isDatabasePostgreSQL
    ? ['void', 'integer', 'bigint', 'text', 'varchar', 'boolean', 'date', 'timestamp', 'numeric', 'json', 'jsonb', 'TRIGGER', 'TABLE']
    : ['INT', 'BIGINT', 'VARCHAR(255)', 'TEXT', 'BOOLEAN', 'DATE', 'DATETIME', 'DECIMAL', 'JSON']

  const availableLanguages = isDatabasePostgreSQL
    ? ['plpgsql', 'sql', 'plpython3u', 'plperl']
    : ['sql']

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center">
          <div className="text-2xl mr-3">⚙️</div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Create Function
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Create a new database function to encapsulate reusable logic
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-4xl mx-auto">
          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-6">
            {/* Basic Information */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Basic Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Function Name *
                  </label>
                  <input
                    type="text"
                    value={functionName}
                    onChange={(e) => { setFunctionName(e.target.value) }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="calculate_total"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Return Type
                  </label>
                  <select
                    value={returnType}
                    onChange={(e) => { setReturnType(e.target.value) }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {returnTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Language
                  </label>
                  <select
                    value={language}
                    onChange={(e) => { setLanguage(e.target.value) }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {availableLanguages.map((lang) => (
                      <option key={lang} value={lang}>
                        {lang.toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Parameters */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Parameters
                </h3>
                <button
                  type="button"
                  onClick={addParameter}
                  className="flex items-center px-3 py-1 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                >
                  <PlusIcon className="w-4 h-4 mr-1" />
                  Add Parameter
                </button>
              </div>

              {parameters.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                  No parameters defined. Click "Add Parameter" to add function parameters.
                </p>
              ) : (
                <div className="space-y-3">
                  {parameters.map((parameter, index) => (
                    <div key={index} className="flex items-center gap-3">
                      {isDatabasePostgreSQL && (
                        <div className="w-20">
                          <select
                            value={parameter.mode}
                            onChange={(e) => { updateParameter(index, 'mode', e.target.value) }}
                            className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                          >
                            <option value="IN">IN</option>
                            <option value="OUT">OUT</option>
                            <option value="INOUT">INOUT</option>
                          </select>
                        </div>
                      )}
                      
                      <div className="flex-1">
                        <input
                          type="text"
                          value={parameter.name}
                          onChange={(e) => { updateParameter(index, 'name', e.target.value) }}
                          placeholder="parameter_name"
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                          required
                        />
                      </div>
                      
                      <div className="w-32">
                        <select
                          value={parameter.type}
                          onChange={(e) => { updateParameter(index, 'type', e.target.value) }}
                          className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                        >
                          {commonDataTypes.map((type) => (
                            <option key={type} value={type}>
                              {type}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="w-24">
                        <input
                          type="text"
                          value={parameter.defaultValue || ''}
                          onChange={(e) => { updateParameter(index, 'defaultValue', e.target.value) }}
                          placeholder="Default"
                          className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                        />
                      </div>

                      <button
                        type="button"
                        onClick={() => { removeParameter(index) }}
                        className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Function Body */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Function Body
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Function Definition *
                </label>
                <textarea
                  value={functionBody}
                  onChange={(e) => { setFunctionBody(e.target.value) }}
                  rows={12}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                  placeholder={isDatabasePostgreSQL 
                    ? `BEGIN
    -- Your function logic here
    -- Use parameters like: parameter_name
    -- Return value using: RETURN result;
    
    DECLARE
        result INTEGER;
    BEGIN
        -- Example calculation
        result := parameter1 + parameter2;
        RETURN result;
    END;`
                    : `-- Your function logic here
    -- Use parameters like: parameter_name
    -- Return value using: RETURN result;
    
    DECLARE result INT;
    SET result = parameter1 + parameter2;
    RETURN result;`
                  }
                  required
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {isDatabasePostgreSQL 
                    ? "Write the function body in PL/pgSQL. Use DECLARE for variables, BEGIN/END for blocks, and RETURN for return values."
                    : "Write the function body in SQL. Use DECLARE for variables and RETURN for return values."
                  }
                </p>
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-start">
                <InformationCircleIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
                <div className="text-sm text-blue-800 dark:text-blue-200">
                  <p className="font-medium mb-1">Function Guidelines:</p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>Functions can take parameters and return values for reusable logic</li>
                    <li>Use IN parameters for input values, OUT for output values</li>
                    <li>TRIGGER return type is used for trigger functions</li>
                    <li>TABLE return type allows returning result sets</li>
                    {isDatabasePostgreSQL && <li>PL/pgSQL supports variables, loops, conditionals, and exception handling</li>}
                    {isDatabaseMySQL && <li>MySQL functions must be DETERMINISTIC or specify appropriate characteristics</li>}
                  </ul>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={onFunctionCreated}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? 'Creating...' : 'Create Function'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
